import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../constants';

interface Conversation {
    id: string;
    participants: {
        id: string;
        username?: string;
        email?: string;
        avatar?: string;
    }[];
    lastMessage: {
        content: string;
        createdAt: string;
        senderId: string;
    };
    unreadCount: number;
}

interface Message {
    id: string;
    content: string;
    senderId: string;
    createdAt: string;
    sender: {
        id: string;
        username?: string;
        email?: string;
        avatar?: string;
    };
}

const MessagesPage: React.FC = () => {
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [sendingMessage, setSendingMessage] = useState(false);
    const { user, token } = useAuth();

    useEffect(() => {
        // For MVP, show placeholder since chat system is not yet implemented in backend
        setLoading(false);
    }, []);

    const getOtherParticipant = (conversation: Conversation) => {
        return conversation.participants.find(p => p.id !== user?.id);
    };

    const formatTime = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

        if (diffInHours < 1) return 'Just now';
        if (diffInHours < 24) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
        return date.toLocaleDateString();
    };

    const handleSendMessage = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newMessage.trim() || !selectedConversation) return;

        setSendingMessage(true);
        // Simulate sending message for MVP
        setTimeout(() => {
            setSendingMessage(false);
            setNewMessage('');
        }, 1000);
    };

    // Mock data for demonstration
    const mockConversations: Conversation[] = [
        {
            id: '1',
            participants: [
                { id: user?.id || '', username: user?.username, email: user?.email },
                { id: '2', username: 'Sarah_Creator', email: 'sarah@example.com', avatar: '/assets/images/user.png' }
            ],
            lastMessage: {
                content: 'Thanks for subscribing! 🎉',
                createdAt: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
                senderId: '2'
            },
            unreadCount: 1
        },
        {
            id: '2',
            participants: [
                { id: user?.id || '', username: user?.username, email: user?.email },
                { id: '3', username: 'Alex_Artist', email: 'alex@example.com', avatar: '/assets/images/user.png' }
            ],
            lastMessage: {
                content: 'New content coming soon!',
                createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
                senderId: '3'
            },
            unreadCount: 0
        }
    ];

    const mockMessages: Message[] = [
        {
            id: '1',
            content: 'Hi! Thanks for subscribing to my content! 🎉',
            senderId: '2',
            createdAt: new Date(Date.now() - 1000 * 60 * 35).toISOString(),
            sender: { id: '2', username: 'Sarah_Creator', avatar: '/assets/images/user.png' }
        },
        {
            id: '2',
            content: 'You\'re welcome! Looking forward to your content.',
            senderId: user?.id || '',
            createdAt: new Date(Date.now() - 1000 * 60 * 32).toISOString(),
            sender: { id: user?.id || '', username: user?.username }
        },
        {
            id: '3',
            content: 'Thanks for subscribing! 🎉',
            senderId: '2',
            createdAt: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
            sender: { id: '2', username: 'Sarah_Creator', avatar: '/assets/images/user.png' }
        }
    ];

    return (
        <div className="container-fluid py-2">
            <div className="row">
                <div className="col-12 col-lg-4 col-xl-4">
                    <div className="card shadow-xss w-100 d-block border-0 p-0 mb-3">
                        <div className="card-body p-4 border-bottom">
                            <h2 className="fw-700 mb-0 mt-0 font-md text-grey-900">Messages</h2>
                        </div>

                        {loading ? (
                            <div className="text-center p-4">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : (
                            <div className="card-body p-0">
                                {mockConversations.length === 0 ? (
                                    <div className="text-center p-4">
                                        <i className="feather-message-circle font-xl text-grey-500 mb-3"></i>
                                        <h4 className="fw-700 text-grey-900">No messages yet</h4>
                                        <p className="text-grey-500 font-xsss">Start a conversation with creators</p>
                                    </div>
                                ) : (
                                    mockConversations.map((conversation) => {
                                        const otherUser = getOtherParticipant(conversation);
                                        return (
                                            <div
                                                key={conversation.id}
                                                className={`d-flex align-items-center p-4 border-bottom cursor-pointer ${selectedConversation === conversation.id ? 'bg-lightblue' : ''}`}
                                                onClick={() => setSelectedConversation(conversation.id)}
                                            >
                                                <div className="me-3">
                                                    <img
                                                        src={otherUser?.avatar || '/assets/images/user.png'}
                                                        alt="User"
                                                        className="rounded-circle"
                                                        style={{ width: '45px', height: '45px', objectFit: 'cover' }}
                                                    />
                                                </div>
                                                <div className="flex-grow-1">
                                                    <h4 className="fw-600 font-xsss mb-1 text-grey-900">
                                                        {otherUser?.username || otherUser?.email?.split('@')[0] || 'Unknown User'}
                                                    </h4>
                                                    <p className="font-xsss mb-0 text-grey-500 text-truncate" style={{ maxWidth: '200px' }}>
                                                        {conversation.lastMessage.content}
                                                    </p>
                                                </div>
                                                <div className="text-end">
                                                    <span className="font-xssss text-grey-500 d-block">
                                                        {formatTime(conversation.lastMessage.createdAt)}
                                                    </span>
                                                    {conversation.unreadCount > 0 && (
                                                        <span className="bg-current text-white rounded-circle d-inline-block text-center mt-1"
                                                            style={{ width: '18px', height: '18px', fontSize: '10px', lineHeight: '18px' }}>
                                                            {conversation.unreadCount}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className="col-12 col-lg-8 col-xl-8">
                    <div className="card shadow-xss w-100 d-block border-0 p-0 mb-3" style={{ height: '600px' }}>
                        {selectedConversation ? (
                            <>
                                <div className="card-body p-4 border-bottom">
                                    {(() => {
                                        const conversation = mockConversations.find(c => c.id === selectedConversation);
                                        const otherUser = conversation ? getOtherParticipant(conversation) : null;
                                        return (
                                            <div className="d-flex align-items-center">
                                                <img
                                                    src={otherUser?.avatar || '/assets/images/user.png'}
                                                    alt="User"
                                                    className="rounded-circle me-3"
                                                    style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                                />
                                                <h4 className="fw-600 font-sm mb-0 text-grey-900">
                                                    {otherUser?.username || otherUser?.email?.split('@')[0] || 'Unknown User'}
                                                </h4>
                                            </div>
                                        );
                                    })()}
                                </div>

                                <div className="card-body p-0" style={{ height: '450px', overflowY: 'auto' }}>
                                    <div className="p-4">
                                        {mockMessages.map((message) => (
                                            <div key={message.id} className={`d-flex mb-3 ${message.senderId === user?.id ? 'justify-content-end' : ''}`}>
                                                {message.senderId !== user?.id && (
                                                    <img
                                                        src={message.sender.avatar || '/assets/images/user.png'}
                                                        alt="User"
                                                        className="rounded-circle me-3"
                                                        style={{ width: '35px', height: '35px', objectFit: 'cover' }}
                                                    />
                                                )}
                                                <div className={`p-3 rounded-3 ${message.senderId === user?.id ? 'bg-current text-white' : 'bg-greylight'}`}
                                                    style={{ maxWidth: '70%' }}>
                                                    <p className="mb-1 font-xsss">{message.content}</p>
                                                    <span className={`font-xssss ${message.senderId === user?.id ? 'text-white-50' : 'text-grey-500'}`}>
                                                        {formatTime(message.createdAt)}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="card-body p-4 border-top">
                                    <form onSubmit={handleSendMessage} className="d-flex align-items-center">
                                        <input
                                            type="text"
                                            className="form-control border-0 bg-greylight font-xsss"
                                            placeholder="Type a message..."
                                            value={newMessage}
                                            onChange={(e) => setNewMessage(e.target.value)}
                                            disabled={sendingMessage}
                                        />
                                        <button
                                            type="submit"
                                            className="btn bg-current text-white font-xsss fw-600 p-3 ms-2 rounded-3"
                                            disabled={sendingMessage || !newMessage.trim()}
                                        >
                                            {sendingMessage ? (
                                                <i className="feather-loader"></i>
                                            ) : (
                                                <i className="feather-send"></i>
                                            )}
                                        </button>
                                    </form>
                                </div>
                            </>
                        ) : (
                            <div className="d-flex align-items-center justify-content-center h-100">
                                <div className="text-center">
                                    <i className="feather-message-circle font-xl text-grey-500 mb-3"></i>
                                    <h4 className="fw-700 text-grey-900">Select a conversation</h4>
                                    <p className="text-grey-500 font-xsss">Choose from your existing conversations</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MessagesPage;